import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '#src/atoms/buttons/Button/index.tsx';
import { iconAddressCard } from '#src/atoms/icons/system/iconAddressCard.ts';
import { FormControlLabel } from '#src/atoms/inputs/FormControlLabel/index.tsx';
import { LabelM } from '#src/atoms/typography/index.tsx';
import { UploadInput } from '#src/components/fiatWallet/CreateFiatWallet/DocumentCheck/UploadInput/index.tsx';
import { StepWrapperFromProps } from '#src/components/fiatWallet/CreateFiatWallet/StepWrapper/index.tsx';
import type { FileWithDataURL } from '#src/components/form/UploadFile/useUploadFile.ts';
import { glossary } from '#src/lib/glossary.ts';

export type Props = {
  children: ReactNode;
  file: FileWithDataURL | undefined;
  name: string;
  setFile: (file: FileWithDataURL) => void;
  onBack: () => void;
  onNext: () => void;
};

const StyledFormControlLabel = styled(FormControlLabel)`
  > * {
    width: 100%;
  }
`;

const MIN_FILE_SIZE = 32000;
const MAX_FILE_SIZE = 7000000;

export const FileUploadStep = ({
  children,
  name,
  file,
  setFile,
  onBack,
  onNext,
}: Props) => {
  const fileSizeIsValid =
    file?.file &&
    file.file.size > MIN_FILE_SIZE &&
    file.file.size < MAX_FILE_SIZE;

  return (
    <StepWrapperFromProps
      submitButton={
        <Button size="medium" onClick={onNext} disabled={!fileSizeIsValid}>
          <FormattedMessage {...glossary.continue} />
        </Button>
      }
      onBack={onBack}
    >
      {children}
      {!!file?.file && !fileSizeIsValid && (
        <LabelM color="var(--c-red-400)">
          <FormattedMessage
            defaultMessage="Your uploaded file must be more than {min} and less than {max}"
            values={{
              min: '32 KB',
              max: '7 MB',
            }}
          />
        </LabelM>
      )}
      <StyledFormControlLabel
        label={null}
        control={
          <UploadInput
            name={name}
            documentName=""
            icon={iconAddressCard}
            file={file}
            setFile={setFile}
          />
        }
        labelPlacement="top"
      />
    </StepWrapperFromProps>
  );
};
